import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import type { Obrigacao, RegimeTributario, SimulacaoResultado } from '@sgc/contracts';
import { UFS } from '@sgc/contracts';
import { get, post } from '../lib/api';
import { OPCOES_REGIME, fmtData } from '../lib/formato';
import { Campo, CampoNumero, Sel } from '../ui/campos';
import { Pill, Sec } from '../ui/layout';
import { useToast } from '../ui/toast';

export const Route = createFileRoute('/_app/obrigacoes/simular')({
  validateSearch: z.object({ obrigacaoId: z.string().optional() }),
  component: Simulador,
});

function Simulador() {
  const { obrigacaoId } = Route.useSearch();
  const toast = useToast();
  const obrigacoes = useQuery({ queryKey: ['obrigacoes', '', ''], queryFn: () => get<Obrigacao[]>('/v1/obrigacoes') });
  const [f, setF] = useState({ obrigacaoId: obrigacaoId ?? '', ano: String(new Date().getFullYear()), uf: 'SP', municipioIbge: '', regime: 'SIMPLES' as RegimeTributario | '', anexo: '', quotas: '1', param: '', dataEvento: '' });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const ob = obrigacoes.data?.find((o) => o.id === f.obrigacaoId);
  const eventual = ob?.periodicidade === 'EVENTUAL';
  const pedeParametro = ob?.resumoRegra?.match(/parâmetro (\w+)/)?.[1] ?? null;

  const sim = useMutation({
    mutationFn: () => post<SimulacaoResultado>('/v1/obrigacoes/simular', {
      obrigacaoId: f.obrigacaoId, ano: Number(f.ano), uf: f.uf, municipioIbge: f.municipioIbge ? Number(f.municipioIbge) : null,
      regime: f.regime || null, anexoSimples: f.anexo ? Number(f.anexo) : null, quotas: Number(f.quotas) || 1,
      parametros: pedeParametro && f.param ? { [pedeParametro]: Number(f.param) } : {}, dataEvento: eventual ? f.dataEvento || null : null,
    }),
    onError: (e) => toast.erro(e),
  });
  const r = sim.data;

  return (
    <>
      <div className="topbar"><div><h1>Simulador de prazos</h1><div className="sub">confira os vencimentos que o motor calcula antes de gerar tarefas</div></div></div>
      <div className="conteudo">
        <Sec num={1} titulo="Contexto" sub="obrigação, ano e o cliente hipotético">
          <div className="fgrid">
            <Sel className="c6" rotulo="Obrigação" valor={f.obrigacaoId} vazio="— escolha —" opcoes={(obrigacoes.data ?? []).map((o) => [o.id, `${o.sigla} · ${o.nome}`] as const)} onChange={(v) => set({ obrigacaoId: v })} />
            <Campo className="c2" rotulo="Ano" valor={f.ano} onChange={(v) => set({ ano: v })} type="number" />
            <Sel className="c2" rotulo="UF" valor={f.uf} opcoes={UFS.map((u) => [u, u] as const)} onChange={(v) => set({ uf: v })} />
            <Campo className="c2" rotulo="Município (IBGE)" valor={f.municipioIbge} onChange={(v) => set({ municipioIbge: v.replace(/\D/g, '') })} inputMode="numeric" />
            <Sel className="c4" rotulo="Regime" valor={f.regime} vazio="— qualquer —" opcoes={OPCOES_REGIME} onChange={(v) => set({ regime: v as RegimeTributario | '' })} />
            {f.regime === 'SIMPLES' && <Sel className="c2" rotulo="Anexo" valor={f.anexo} vazio="—" opcoes={[['1', 'I'], ['2', 'II'], ['3', 'III'], ['4', 'IV'], ['5', 'V']]} onChange={(v) => set({ anexo: v })} />}
            <CampoNumero className="c2" rotulo="Quotas" valor={f.quotas} onChange={(v) => set({ quotas: v })} ajuda="IRPJ trimestral: 1 ou 3" />
            {pedeParametro && <CampoNumero className="c3" rotulo={`Dia do vencimento (${pedeParametro})`} obrigatorio valor={f.param} onChange={(v) => set({ param: v })} ajuda="esta obrigação tem o dia definido pelo município/UF: informe o dia aqui e, no vínculo do cliente, na aba Obrigações" />}
            {eventual && <Campo className="c2" rotulo="Data do evento" valor={f.dataEvento} onChange={(v) => set({ dataEvento: v })} type="date" />}
          </div>
          <div className="linha-acoes">
            <button className="btn primario" type="button" disabled={!f.obrigacaoId || sim.isPending || (!!pedeParametro && !(Number(f.param) >= 1))} onClick={() => sim.mutate()}>Simular</button>
            {ob?.resumoRegra && <span className="mini">regra: <b>{ob.resumoRegra}</b></span>}
          </div>
        </Sec>

        {r && (
          <Sec num={2} titulo={`${r.obrigacao.sigla} — ${f.ano}`} sub={r.obrigacao.nome} acoes={<span className="mini mono">calendário {r.calendarioHash}</span>}>
            {r.erro && <div className="alerta erro">{r.erro}</div>}
            {!r.erro && (
              <>
                <div className="tbwrap">
                  <table className="tb">
                    <thead><tr><th>Competência</th><th>Parc.</th><th>Data-base</th><th>Vencimento</th><th>Dia</th><th>Prazo interno</th><th>Abre em</th><th>Ajuste / observação</th><th>Fundamento</th></tr></thead>
                    <tbody>
                      {r.linhas.map((l, i) => (
                        <tr key={i}>
                          <td><b>{l.competenciaLabel}</b>{l.varianteDescricao && <div className="dim">{l.varianteDescricao}</div>}</td>
                          <td className="c">{l.parcelaNumero}</td>
                          <td className="mono dim">{fmtData(l.dataBase)}</td>
                          <td className="mono"><b>{fmtData(l.vencimentoEfetivo)}</b>{l.origem === 'PRORROGACAO' && <> <Pill cor="ambar">prorrogado</Pill><div className="dim">legal: {fmtData(l.vencimentoLegal)}</div></>}</td>
                          <td>{l.diaSemana}</td>
                          <td className="mono">{fmtData(l.dataLimiteInterna)}</td>
                          <td className="mono dim">{fmtData(l.dataAbertura)}</td>
                          <td className="dim">{l.ajuste ?? ''}</td>
                          <td className="dim">{l.fundamentoLegal ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {r.feriadosConsiderados.length > 0 && (
                  <div className="sec-info">
                    <b>feriados cadastrados considerados:</b>
                    {r.feriadosConsiderados.map((h) => <span key={h.data}>{fmtData(h.data)} {h.nome}</span>)}
                  </div>
                )}
                <div className="fnote" style={{ marginTop: 10 }}>Fins de semana e feriados nacionais por lei (inclusive Sexta-feira Santa) entram sempre; Carnaval, Corpus Christi, 24 e 31/12 só quando a regra usa o calendário bancário.</div>
              </>
            )}
          </Sec>
        )}
      </div>
    </>
  );
}
